import { addDays, format } from 'date-fns';
import { formatInTimeZone, utcToZonedTime } from 'date-fns-tz';
import { getBusinessTimezone } from './businessTimezone';
import restaurantTimeAvailability from './restaurantTimeAvailability';

// Epoch windows are authoritative; display labels and device dates are not.
export const timingKey = (restaurant, type, time) => {
  if (!restaurant || !type || !time) return null;
  if (time.type === 'asap')
    return JSON.stringify([String(restaurant), type, 'asap']);
  if (time.type !== 'target') return null;
  const start = Number(time.value?.start);
  const end = Number(time.value?.end);
  if (
    !Number.isFinite(start) ||
    !Number.isFinite(end) ||
    start <= 0 ||
    end > 8640000000000 ||
    end <= start
  )
    return null;
  return JSON.stringify([String(restaurant), type, start, end]);
};

export const getOrderTiming = (state, now = Date.now()) => {
  const restaurant = state.restaurant?.selectedRestaurantId;
  const type = state.order?.type?.value;
  const time = state.time?.selectedTime;
  const key = timingKey(restaurant, type, time);
  const expired =
    time?.type !== 'asap' && Number(time?.value?.end) <= now / 1000;
  const deliberate =
    !!key &&
    (state.time?.explicitSelection === timingKey('selection', type, time) ||
      state.time?.confirmedContext === key);
  const unavailable =
    !!key &&
    !expired &&
    time?.type !== 'asap' &&
    !restaurantTimeAvailability(state.restaurant, type, time, now);
  const requiresConfirmation =
    !key ||
    expired ||
    unavailable ||
    !!state.time?.reviewRequired ||
    (time?.type !== 'asap' && !deliberate);
  const checkoutMatches =
    !!key &&
    !!state.order?.checkoutData?.id &&
    !state.order?.checkoutData?.isEnded &&
    state.time?.checkoutContext?.key === key &&
    state.time?.checkoutContext?.id === state.order?.checkoutData?.id;
  return {
    key,
    requiresConfirmation,
    checkoutMatches,
    expired,
    unavailable,
    reviewRequired: !!state.time?.reviewRequired,
  };
};

// Compare and format calendar days in the business timezone, not the device's.
const formatTimingWindow = (start, end, timezone, now, includeDay) => {
  const date = new Date(start * 1000);
  const endDate = new Date(end * 1000);
  const dayKey = (value) => formatInTimeZone(value, timezone, 'yyyy-MM-dd');
  const today = utcToZonedTime(now, timezone);
  const dayLabel = (value) => {
    const day = dayKey(value);
    if (day === format(today, 'yyyy-MM-dd')) return 'Today';
    if (day === format(addDays(today, 1), 'yyyy-MM-dd')) return 'Tomorrow';
    return formatInTimeZone(value, timezone, 'EEE, MMM d');
  };
  const sameDay = dayKey(date) === dayKey(endDate);
  const sameMeridiem =
    formatInTimeZone(date, timezone, 'a') ===
    formatInTimeZone(endDate, timezone, 'a');
  const startTime = formatInTimeZone(
    date,
    timezone,
    sameDay && sameMeridiem ? 'h:mm' : 'h:mm a'
  );
  const endTime = formatInTimeZone(endDate, timezone, 'h:mm a');
  if (!sameDay)
    return `${dayLabel(date)} · ${startTime}–${dayLabel(endDate)} · ${endTime}`;
  return `${includeDay ? `${dayLabel(date)} · ` : ''}${startTime}–${endTime}`;
};

export const formatOrderTiming = (
  time,
  type,
  restaurantTimezone,
  now = new Date(),
  { includeMode = true, includeAsapWindow = false } = {}
) => {
  const mode = type === 'takeout' ? 'Takeout' : 'Delivery';
  const prefix = includeMode ? `${mode} · ` : '';
  if (!time) return `${prefix}Choose time`;
  const timezone = getBusinessTimezone(restaurantTimezone);
  if (time.type === 'asap') {
    if (
      includeAsapWindow &&
      Number(time.value?.end) > now.getTime() / 1000 &&
      Number(time.value?.start) >= now.getTime() / 1000 &&
      Number(time.value?.end) <= 8640000000000 &&
      Number(time.value?.end) > Number(time.value?.start)
    ) {
      return `${prefix}ASAP · ${formatTimingWindow(
        Number(time.value.start),
        Number(time.value.end),
        timezone,
        now,
        false
      )}`;
    }
    return `${prefix}ASAP`;
  }
  const start = Number(time.value?.start);
  const end = Number(time.value?.end);
  if (
    time.type !== 'target' ||
    !Number.isFinite(start) ||
    !Number.isFinite(end) ||
    start <= 0 ||
    end > 8640000000000 ||
    end <= start
  )
    return `${prefix}Choose time`;
  return `${prefix}${formatTimingWindow(start, end, timezone, now, true)}`;
};
